import '@/assets/styles/components/register-form.styles.scss';
import React, { useState, useEffect } from 'react';
import { Stepper } from '@/components';
import { RegisterSteps } from './register-form.data';
import GET_STEPS from './steps';
import { STEPS, CONTENT_STATES, INITIAL_CONTENT_STATES } from './register-form.constants';
import { CombinedFormData, StepContents } from './register-form.types';
import { useNavigate, useLocation } from 'react-router';
import { useApi } from '@/api/useApi';
import { useQueryProvider } from '@/hooks';
import { NavigationPath } from '@/router/navigation-path.ts';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { TimeSpan } from '@/utility/time-span.ts';

const getGraduationType = (graduationLevel?: string) => {
	if (!graduationLevel) return '';

	switch (graduationLevel) {
		case 'POSTGRADUATE_DEGREE':
			return 'master';
		case 'ONGOING_POSTGRADUATE':
			return 'ongoing_master';
		default:
			return 'bachelor';
	}
};

export const OnboardingForm: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { ExpertApplicationControllerApi, ExpertApplicantControllerApi } = useApi();

	const [currentStep, setCurrentStep] = useState(STEPS.FIRST);
	const [completedSteps, setCompletedSteps] = useState<number[]>([]);
	const [currentContent, setCurrentContent] = useState(CONTENT_STATES.PERSONAL_INFORMATION);
	const [formData, setFormData] = useState<CombinedFormData>({
		...location.state?.data
	});

	const { data: applicationProgress, isLoading: isProgressLoading } = useQueryProvider([QUERY_KEYS.getApplicationStepProgress], () => ExpertApplicationControllerApi.getApplicationStepProgress(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false
	});

	useQueryProvider([QUERY_KEYS.getLoggedInExpertApplicantDetailInfo], () => ExpertApplicantControllerApi.getLoggedInExpertApplicantDetailInfo(), {
		refetchOnWindowFocus: false,
		select: ({ data }) => data.data,
		onSuccess: ({ expertApplicant }) => {
			if (expertApplicant) {
				const birthday = expertApplicant.birthday ? new Date(expertApplicant.birthday) : null;

				setFormData({
					userType: expertApplicant.specializationType || defaultFormData.userType,
					firstName: expertApplicant.name || '',
					lastName: expertApplicant.surname || '',
					phone: expertApplicant.phone || '',
					email: expertApplicant.email || '',
					acceptTerms: expertApplicant.commercialConfirm || false,
					province: expertApplicant.cityCode || '',
					district: expertApplicant.districtCode || '',
					type: getGraduationType(expertApplicant.graduationLevel),
					university: expertApplicant.universityName || '',
					department: expertApplicant.departmentName || '',
					birthDay: birthday ? String(birthday.getDate()).padStart(2, '0') : '',
					birthMonth: birthday ? String(birthday.getMonth() + 1).padStart(2, '0') : '',
					birthYear: birthday ? String(birthday.getFullYear()) : '',
					bachelorUniversity: expertApplicant.graduateUniversityId || '',
					bachelorDepartment: expertApplicant.graduateDepartmentId || '',
					masterUniversity: '',
					masterDepartment: '',
					ongoingUniversity: '',
					ongoingDepartment: '',
					studentDocument: undefined,
					examDocument: undefined,
					bachelorDegree: undefined,
					masterDegree: undefined,
					studentCertificate: undefined,
					supportAreas: [],
					workStatus: ''
				});
			}
		}
	});

	useEffect(() => {
		if (applicationProgress) {
			const completed = applicationProgress?.filter(step => step.isCompleted)?.map(step => step.applicationStepId);

			setCompletedSteps(completed);

			const firstUncompleted = applicationProgress?.find(step => !step.isCompleted);
			if (firstUncompleted) {
				setCurrentStep(firstUncompleted.applicationStepId);
				setCurrentContent(INITIAL_CONTENT_STATES[firstUncompleted.applicationStepId]);
			}
		}
	}, [applicationProgress]);

	// TODO: After the backend is ready, this function will be used to navigate between steps
	/*
  const handleStepChange = (step: number) => {
    const isAccessible =
            step === STEPS.FIRST ||
            completedSteps.includes(step) ||
            step === currentStep ||
            completedSteps.includes(step - 1);

    if (isAccessible) {
      setCurrentStep(step);
      setCurrentContent(INITIAL_CONTENT_STATES[step]);
    }
  };
  */

	const stepContents: StepContents = {
		[STEPS.FIRST]: {
			[CONTENT_STATES.PERSONAL_INFORMATION]: (
				<GET_STEPS.PersonalInfo
					formData={formData}
					onFormDataChange={data => setFormData(prev => ({ ...prev, ...data }))}
					onNext={() => {
						const newCompletedSteps = [...new Set([...completedSteps, STEPS.FIRST])];
						setCompletedSteps(newCompletedSteps);
						setCurrentStep(STEPS.SECOND);
						setCurrentContent(INITIAL_CONTENT_STATES[STEPS.SECOND]);
					}}
				/>
			)
		},
		[STEPS.SECOND]: {
			[CONTENT_STATES.EDUCATION_INFORMATION]: (
				<GET_STEPS.EducationInfo
					formData={formData}
					onFormDataChange={data => setFormData(prev => ({ ...prev, ...data }))}
					onNext={() => {
						const newCompletedSteps = [...new Set([...completedSteps, STEPS.SECOND])];
						setCompletedSteps(newCompletedSteps);
						setCurrentStep(STEPS.THIRD);
						setCurrentContent(INITIAL_CONTENT_STATES[STEPS.THIRD]);
					}}
				/>
			)
		},
		[STEPS.THIRD]: {
			[CONTENT_STATES.WORK_INFORMATION]: (
				<GET_STEPS.WorkInfo
					formData={formData}
					onFormDataChange={data => setFormData(prev => ({ ...prev, ...data }))}
					onNext={() => setTimeout(() => navigate(NavigationPath.applicationReview), TimeSpan.milliSecond.fiveHundred)}
				/>
			)
		}
	};

	if (isProgressLoading || !applicationProgress) {
		return (
			<div className="register-container">
				<Stepper
					steps={RegisterSteps}
					currentStep={1}
					completedSteps={[]}
					stepsProgress={{ 1: 0, 2: 0, 3: 0 }}
					onStepClick={() => null}
				/>
			</div>
		);
	}

	return (
		<div className="register-container">
			<Stepper
				steps={RegisterSteps}
				currentStep={currentStep}
				completedSteps={completedSteps}
				stepsProgress={{
					1: completedSteps.includes(1) ? 100 : 0,
					2: completedSteps.includes(2) ? 100 : 0,
					3: completedSteps.includes(3) ? 100 : 0
				}}
				onStepClick={() => null}
			/>
			<div className="form-container">{stepContents[currentStep][currentContent]}</div>
		</div>
	);
};
